import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addComment,
  blockComment,
  checkReportedBefore,
  deleteComment,
  deletePost,
  editComment,
  getBoardDataWithComment,
  reportComment,
  updateLikeByCommunity,
} from "firebase_/firebase_function/firebase_community";
import { getBlockData } from "firebase_/firebase_function/firebase_user";
import { emptyCheck, maskMiddleCharacters } from "util/function/function";
import { useUser } from "util/context/user_context";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import moment from "moment";
import CommentBox from "components/community/comment_box";
import ConfirmDialog from "components/confirm_dialog";
import ReportBoardDialog from "components/community/dialog/report_modal_board";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";
import OnlyLoadingScreen from "components/loading_screen";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  red: "#EF4444",
  amber800: "#92400E",
  amber100: "#FEF3C7",
};

const STORY_TYPE = "농사 이야기";

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return "";
  const diffMin = moment().diff(m, "minutes");
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = moment().diff(m, "hours");
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = moment().diff(m, "days");
  if (diffDay < 7) return `${diffDay}일 전`;
  return m.format("YYYY.MM.DD HH:mm");
};

function Card({ children, style }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: 24,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CategoryBadge({ type }) {
  const isInquiry = type !== STORY_TYPE;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 12px",
        borderRadius: 9999,
        fontSize: 12,
        fontWeight: 700,
        background: isInquiry ? C.amber100 : C.greenBg,
        color: isInquiry ? C.amber800 : C.greenDark,
        whiteSpace: "nowrap",
      }}
    >
      {isInquiry ? "고장 문의" : "농사 이야기"}
    </span>
  );
}

function Avatar({ name, size = 40 }) {
  const ch = (name || "익").trim().charAt(0);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 9999,
        background: C.greenBg,
        color: C.greenDark,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.42,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {ch}
    </div>
  );
}

function HeartIconSvg({ active, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={active ? C.red : "none"} stroke={active ? C.red : C.sub} strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function CommentIconSvg({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={C.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function MoreMenu({ items, onClose }) {
  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        right: 0,
        minWidth: 120,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        zIndex: 100,
        overflow: "hidden",
      }}
    >
      {items.map((it, i) => (
        <button
          key={it.label}
          type="button"
          onClick={() => {
            it.onClick();
            onClose();
          }}
          style={{
            width: "100%",
            padding: "10px 14px",
            textAlign: "left",
            background: "#fff",
            border: "none",
            borderTop: i === 0 ? "none" : `1px solid ${C.line}`,
            cursor: "pointer",
            fontSize: 14,
            color: it.danger ? C.red : C.body,
            fontWeight: 500,
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function ImageGallery({ urls = [] }) {
  if (!urls?.length) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: urls.length === 1 ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
      {urls.map((u, i) => (
        <a
          key={i}
          href={u}
          data-fancybox="gallery"
          data-caption={`${i + 1}/${urls.length}`}
          style={{ display: "block", aspectRatio: urls.length === 1 ? "16/9" : "1/1", borderRadius: 12, overflow: "hidden", background: "#F3F4F6", border: `1px solid ${C.line}` }}
        >
          <img
            src={u}
            alt=""
            draggable={false}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </a>
      ))}
    </div>
  );
}

export default function DesktopCommunityDetailScreen() {
  const navigate = useNavigate();
  const { docId } = useParams();
  const { userData, setBlockData } = useUser();

  const [boardData, setBoardData] = useState({});
  const [commentValue, setCommentValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const initRef = useRef(true);

  // 메뉴 / 모달 상태
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const [reportModal, setReportModal] = useState(false);
  const [confirmBoardBlockModal, setConfirmBoardBlockModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [onlyConfirmDialog, setOnlyConfirmDialog] = useState(false);
  const [message, setMessage] = useState("");
  const [notPageModal, setNotPageModal] = useState(false);

  useEffect(() => {
    Fancybox.bind("[data-fancybox]", {});
    return () => {
      Fancybox.destroy();
    };
  }, [boardData]);

  useEffect(() => {
    const onClick = (e) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showMenu]);

  useEffect(() => {
    if (docId && userData) {
      getBlockData(userData.docId, setBlockData);
      fetchBoardData();
    }
    return () => setBoardData({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, userData?.docId]);

  const fetchBoardData = async () => {
    if (initRef.current) {
      setInitLoading(true);
      initRef.current = false;
    }
    const data = await getBoardDataWithComment(docId);
    if (emptyCheck(data?.board)) {
      setMessage("게시글이 존재하지 않습니다.");
      setNotPageModal(true);
      return;
    }
    setBoardData(data);
    setInitLoading(false);
  };

  const board = boardData?.board || {};
  const comments = boardData?.comment || [];
  const isMine = board?.id && board.id === userData?.id;
  const liked = useMemo(
    () => Array.isArray(board?.liked) && board.liked.includes(userData?.id),
    [board?.liked, userData?.id],
  );

  const pressedLike = async () => {
    if (await updateLikeByCommunity(docId, userData.id)) fetchBoardData();
  };

  const pressedComment = async () => {
    if (commentValue.trim() === "") return;
    setLoading(true);
    try {
      const ok = await addComment(userData.id, userData.name, commentValue, docId);
      if (ok) {
        await fetchBoardData();
        setCommentValue("");
      }
    } finally {
      setLoading(false);
    }
  };

  const pressedEdit = async (editValue, commentDocId) => {
    if (editValue.trim() === "") return;
    setLoading(true);
    try {
      if (await editComment(docId, editValue, commentDocId)) await fetchBoardData();
    } finally {
      setLoading(false);
    }
  };

  const pressedDelete = async (commentDocId) => {
    if (await deleteComment(docId, commentDocId)) fetchBoardData();
  };

  const pressedBlockBoard = async (targetUserId, targetUserName, userId, userDocId) => {
    if (await blockComment(targetUserId, targetUserName, userId, userDocId)) {
      await getBlockData(userDocId, setBlockData);
      navigate("/community");
    }
  };

  const pressedBlock = async (targetUserId, targetUserName, userId, userDocId) => {
    if (await blockComment(targetUserId, targetUserName, userId, userDocId)) {
      fetchBoardData();
      getBlockData(userDocId, setBlockData);
    }
  };

  const pressedReport = async (userId, userName, reason) => {
    if (await reportComment(userId, userName, reason, userData.id)) fetchBoardData();
  };

  const pressedDeletePost = async () => {
    setLoading(true);
    try {
      if (await deletePost(docId)) navigate("/community");
    } finally {
      setLoading(false);
    }
  };

  const handleMenuItems = () => {
    if (isMine) {
      return [
        { label: "수정", onClick: () => navigate(`/community/edit/${docId}`) },
        { label: "삭제", onClick: () => setDeleteModal(true), danger: true },
      ];
    }
    return [
      {
        label: "신고",
        onClick: async () => {
          const already = await checkReportedBefore(board.id, userData.id);
          if (already) {
            setMessage("이미 신고된 유저입니다.");
            setOnlyConfirmDialog(true);
          } else {
            setReportModal(true);
          }
        },
      },
      { label: "차단", onClick: () => setConfirmBoardBlockModal(true), danger: true },
    ];
  };

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 880,
        margin: "0 auto",
        padding: "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      <button
        type="button"
        onClick={() => navigate("/community")}
        style={{
          alignSelf: "flex-start",
          padding: "6px 12px",
          background: "transparent",
          border: `1px solid ${C.line}`,
          borderRadius: 9999,
          color: C.body,
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        ← 목록
      </button>

      {initLoading ? (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ height: 14, width: "20%", background: "#F3F4F6", borderRadius: 6 }} />
            <div style={{ height: 24, width: "70%", background: "#F3F4F6", borderRadius: 6 }} />
            <div style={{ height: 14, width: "30%", background: "#F3F4F6", borderRadius: 6 }} />
            <div style={{ height: 220, width: "100%", background: "#F3F4F6", borderRadius: 12 }} />
          </div>
        </Card>
      ) : (
        <>
          {/* 본문 카드 */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <CategoryBadge type={board.type} />
                <span style={{ fontSize: 13, color: C.sub }}>
                  {relativeTime(board.createDate)}
                </span>
              </div>
              {/* 메뉴 */}
              <div ref={menuRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setShowMenu((p) => !p)}
                  aria-label="메뉴"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9999,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: C.sub,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="6" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="12" cy="18" r="1.5" />
                  </svg>
                </button>
                {showMenu && (
                  <MoreMenu items={handleMenuItems()} onClose={() => setShowMenu(false)} />
                )}
              </div>
            </div>

            <div style={{ height: 14 }} />
            <h1
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: C.ink,
                letterSpacing: "-0.02em",
                margin: 0,
                wordBreak: "break-word",
              }}
            >
              {board.title}
            </h1>
            <div style={{ height: 12 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar name={board.name} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
                  {maskMiddleCharacters(board.name) || "익명"}
                </span>
              </div>
            </div>

            <div style={{ height: 24 }} />
            <div
              style={{
                fontSize: 16,
                color: C.body,
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {board.body}
            </div>

            {Array.isArray(board.imageUrls) && board.imageUrls.length > 0 && (
              <>
                <div style={{ height: 20 }} />
                <ImageGallery urls={board.imageUrls} />
              </>
            )}

            <div style={{ height: 20 }} />
            <div style={{ height: 1, background: C.line }} />
            <div style={{ height: 14 }} />
            <div style={{ display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={pressedLike}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 9999,
                  border: `1px solid ${liked ? "#FECACA" : C.line}`,
                  background: liked ? "#FEF2F2" : "#fff",
                  color: liked ? C.red : C.body,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <HeartIconSvg active={liked} />
                좋아요 {board.likes ?? 0}
              </button>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 9999,
                  border: `1px solid ${C.line}`,
                  color: C.body,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <CommentIconSvg />
                댓글 {board.commentCount ?? 0}
              </div>
            </div>
          </Card>

          {/* 댓글 카드 */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CommentIconSvg size={20} />
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: C.ink,
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                댓글 {comments.length}
              </h2>
            </div>
            <div style={{ height: 16 }} />

            {comments.length === 0 ? (
              <div
                style={{
                  padding: "32px 16px",
                  textAlign: "center",
                  color: C.sub,
                  fontSize: 14,
                  background: "#FAFAFA",
                  borderRadius: 12,
                }}
              >
                아직 댓글이 없어요. 첫 댓글을 남겨보세요.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {comments.map((c) => (
                  <CommentBox
                    key={c.docId}
                    comment={c}
                    pressedEdit={pressedEdit}
                    pressedDelete={pressedDelete}
                    pressedReport={pressedReport}
                    pressedBlock={pressedBlock}
                  />
                ))}
              </div>
            )}

            <div style={{ height: 20 }} />
            {/* 댓글 입력 */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                background: "#FAFAFA",
                border: `1px solid ${C.line}`,
                borderRadius: 12,
                padding: 14,
              }}
            >
              <textarea
                value={commentValue}
                onChange={(e) => {
                  if (e.target.value.length > 100) {
                    setMessage("댓글은 100글자 이내로 작성해주세요.");
                    setOnlyConfirmDialog(true);
                    return;
                  }
                  setCommentValue(e.target.value);
                }}
                placeholder="따뜻한 댓글을 남겨주세요"
                style={{
                  minHeight: 76,
                  width: "100%",
                  resize: "none",
                  outline: "none",
                  border: "none",
                  background: "transparent",
                  fontSize: 14,
                  color: C.ink,
                  lineHeight: 1.6,
                  fontFamily: "inherit",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 12, color: C.sub }}>
                  {commentValue.length}/100
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  {commentValue && (
                    <button
                      type="button"
                      onClick={() => setCommentValue("")}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        background: "#fff",
                        border: `1px solid ${C.line}`,
                        color: C.body,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      취소
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={pressedComment}
                    disabled={!commentValue.trim()}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 8,
                      background: commentValue.trim() ? C.greenDark : "#E5E7EB",
                      border: "none",
                      color: commentValue.trim() ? "#fff" : "#9CA3AF",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: commentValue.trim() ? "pointer" : "not-allowed",
                    }}
                  >
                    등록
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Modals */}
      <ReportBoardDialog
        open={reportModal}
        onClose={() => setReportModal(false)}
        onSubmit={pressedReport}
        targetId={board?.id}
        targetName={board?.name}
      />
      <ConfirmDialog
        open={confirmBoardBlockModal}
        onClose={() => setConfirmBoardBlockModal(false)}
        onConfirm={() => {
          pressedBlockBoard(board.id, board.name, userData.id, userData.docId);
          setConfirmBoardBlockModal(false);
        }}
        message={"차단 하시겠습니까?"}
      />
      <ConfirmDialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={() => {
          pressedDeletePost();
          setDeleteModal(false);
        }}
        message={"삭제 하시겠습니까?"}
      />
      <OnlyConfirmDialog
        open={onlyConfirmDialog}
        onClose={() => setOnlyConfirmDialog(false)}
        message={message}
      />
      <OnlyConfirmDialog
        open={notPageModal}
        onClose={() => {
          setNotPageModal(false);
          navigate("/community");
        }}
        message={message}
      />
      <OnlyLoadingScreen isOpen={loading} />
    </div>
  );
}
